import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraSource, CameraResultType } from '@capacitor/camera';
import getCameraPlugin from '../../utils/cameraPlugin';
import axiosInstance from '../../api/axiosInstance';
import { useSocket } from "../../context/SocketContext";
import { useAuth } from '../../context/AuthContext';
import { base64ToBlob } from '../../utils/mediaUtils';
import { CircularProgress } from '@mui/material';
import SwipeMessage from './parts/SwipeMessage';
import SeenObserver from './parts/SeenObserver';
import ChatHeader from './parts/ChatHeader';
import AudioMessage from './parts/AudioMessage';
import { formatCount } from '../../utils/formatCount';
import { motion } from 'framer-motion';
import { SendHorizonal, CircleX, Undo2, Trash2, Copy, FileText, Paperclip } from 'lucide-react';
import { PhotoIcon, CameraIcon, MicrophoneIcon } from '@heroicons/react/24/solid';
import { HiPhoneArrowUpRight, HiPhoneArrowDownLeft, HiPhoneXMark } from "react-icons/hi2";
import Picker from 'emoji-picker-react';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import OpenMediaViewer from './OpenMediaViewer';
import { compressImage, compressVideo } from '../../utils/mediaCompressor';

export default function ChatWindow() {
  const { socket } = useSocket();
  const { userId } = useAuth();

  const { chatId: rawChatId } = useParams();
  const chatId = rawChatId === 'undefined' ? null : rawChatId;

  const navigate = useNavigate();

  const location = useLocation();
  const fromSettings = location.state?.from === 'settings';

  const [chatInfo, setChatInfo] = useState(() => location.state?.chatInfo ?? null);
  const [messages, setMessages] = useState([]);
  const [showPickerFor, setShowPickerFor] = useState(null);
  const [actionFor, setActionFor] = useState(null);
  const [pickerExpanded, setPickerExpanded] = useState(false);
  const [peerPresence, setPeerPresence] = useState({ status: null, last_active: null });
  const [openMedia, setOpenMedia] = useState(null);
  const PAGE_SIZE = 25;
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Track how many messages we've loaded via history pagination (not counting live new messages)
  const historyCountRef = useRef(0);

  const [newMessage, setNewMessage] = useState('');
  const messagesContainerRef = useRef(null);
  const hasMountedOnce = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  const [replyTo, setReplyTo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const messageRefs = useRef(new Map());
  const pickerObserverRef = useRef(null);
  const composerRef = useRef(null);
  const galleryInputRef = useRef(null);

  const CameraPlugin = getCameraPlugin();

  const createTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Interaction permission: if user left a group, disable reactions/replies/settings
  const hasLeftGroup = !!(chatInfo?.is_group) && !(
    chatInfo?.members?.some(m => m.user_id === userId && !m.left_at)
  );
  const canInteract = !hasLeftGroup;

  //audio messages
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const micButtonRef = useRef(null);
  const [recordingHint, setRecordingHint] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const willDiscardRef = useRef(false);
  const discardRef = useRef(false);
  const holdTimerRef = useRef(null);
  const hasStartedRecordingRef = useRef(false);
  const HOLD_TO_RECORD_MS = 600;

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => {
    if (socket && userId) {
      socket.emit('join rooms', userId);
    }
  }, [socket, userId]);

  useEffect(() => {
    if (!socket) return;

    socket.on('chat message', msg => {
      const isSentByYou = String(msg?.sender) === String(userId) || !!msg?.is_sent_by_you;

      const sender = chatInfo?.members?.find(m => m.user_id === msg.sender);

      setMessages(prev => {
        if (isSentByYou) {
          const sameReply = (a, b) => (!a && !b) || a === b;
          const baseName = (u) => {
            const s = String(u || '');
            const noQuery = s.split('?')[0].split('#')[0];
            const parts = noQuery.split('/');
            return parts[parts.length - 1];
          };
          const safeEq = (a, b) => String(a || '') === String(b || '');

          let idx = prev.findIndex((m) => {
            if (!m.pending) return false;
            if (!m.finalContent) return false;
            const replyMatches = sameReply(m.is_reply_to, msg.is_reply_to);
            const typeMatches = !m.type || !msg.type ? true : m.type === msg.type;
            if (!replyMatches || !typeMatches) return false;

            const fc = String(m.finalContent || '');
            const mc = String(msg?.content || '');
            const fcBase = baseName(fc);
            const mcBase = baseName(mc);

            const contentMatches =
              safeEq(fc, mc) ||
              mc.endsWith(fc) ||
              fc.endsWith(mc) ||
              (fcBase && mcBase && safeEq(fcBase, mcBase));

            const fileNameMatches = m.type === 'file' && (safeEq(m.file_name, msg.file_name));

            return contentMatches || fileNameMatches;
          });
          if (idx === -1) {
            idx = prev.findIndex(
              (m) => m.pending && !m.type && m.content === msg.content && sameReply(m.is_reply_to, msg.is_reply_to)
            );
          }
          if (idx !== -1) {
            const copy = [...prev];
            const original = copy[idx];
            const merged = {
              ...msg,
              ...(msg.file_name ? { file_name: msg.file_name } : {}),
              sender_username: sender?.username || '',
              sender_profile_url: sender?.profile_url || null,
              is_sent_by_you: true,
            };
            if (
              ['audio', 'photo', 'video', 'file'].includes(msg.type || '') &&
              original?.content && typeof original.content === 'string' && original.content.startsWith('blob:')
            ) {
              merged.content = original.content;
            }
            copy[idx] = merged;
            scrollToBottom();
            return copy;
          }
        }
        // If message already exists (e.g., came from fetch and then via socket), update in-place
        const existIdx = prev.findIndex(m => m.message_id === msg.message_id);
        if (existIdx !== -1) {
          const copy = [...prev];
          copy[existIdx] = {
            ...copy[existIdx],
            ...msg,
            ...(msg.file_name ? { file_name: msg.file_name } : {}),
            sender_username: sender?.username || '',
            sender_profile_url: sender?.profile_url || null,
            is_sent_by_you: isSentByYou,
          };
          return copy;
        }

        // Otherwise append as a new message
        scrollToBottom();
        return [
          ...prev,
          {
            ...msg,
            ...(msg.file_name ? { file_name: msg.file_name } : {}),
            sender_username: sender?.username || '',
            sender_profile_url: sender?.profile_url || null,
            is_sent_by_you: isSentByYou,
          },
        ];
      });
    });

    socket.on('presence', ({ userId: presenceUserId, status, last_active }) => {
      const peer = getPeerUser();
      if (peer && presenceUserId === peer.id) {
        setPeerPresence({ status, last_active });
      }
    });

    socket.on('status update', ({ messageId, statuses }) => {
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === messageId
            ? { ...msg, statuses }
            : msg
        )
      );
    });

    socket.on('reaction', ({ messageId, reaction }) => {
      setMessages(prev =>
        prev.map(msg => {
          if (msg.message_id === messageId) {
            const updatedReactions = [...(msg.reactions || []), reaction];
            return { ...msg, reactions: updatedReactions };
          }
          return msg;
        })
      );
    });

    socket.on('unsend', ({ messageId }) => {
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === messageId
            ? { ...msg, type: 'deleted', content: '' }
            : msg
        )
      );
    });

    return () => {
      socket.off('chat message');
      socket.off('presence');
      socket.off('status update');
      socket.off('reaction');
      socket.off('unsend');
    };
  }, [socket, chatInfo]);

  // If the route is `/chat/undefined`, we rely on navigation state for the peer/group info.
  // Hydrate it so the header renders and we can create the chat on first send.
  useEffect(() => {
    const stateChatInfo = location.state?.chatInfo;
    if (!stateChatInfo) return;

    // When chatId is missing (new chat), always prefer the provided state.
    if (!chatId) {
      setChatInfo(stateChatInfo);
      return;
    }

    // When chatId exists, only use it as a fallback before fetchChat loads members/etc.
    setChatInfo((prev) => prev ?? stateChatInfo);
  }, [chatId, location.state]);

  useEffect(() => {
    if (!chatId) return;

    const fetchChat = async () => {
      try {
        // initial page: latest PAGE_SIZE messages
        const res = await axiosInstance.get(`/chat/get_chat/${chatId}?limit=${PAGE_SIZE}&offset=0`);

        setChatInfo(res.data.chatInfo);

        const fetched = res.data.messages || [];
        const preserveMessages = !!location.state?.preserveMessages;
        if (preserveMessages) {
          // Merge fetched history into current state (keeps optimistic/pending messages).
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.message_id));
            const uniqueFetched = fetched.filter((m) => !seen.has(m.message_id));
            return [...uniqueFetched, ...prev];
          });
        } else {
          setMessages(fetched);
        }

        historyCountRef.current = res.data.messages?.length || 0;
        setHasMore((res.data.messages?.length || 0) === PAGE_SIZE);

        const peer = (function() {
          if (res.data?.chatInfo?.user_id) return { id: res.data.chatInfo.user_id };
          const other = res.data?.chatInfo?.members?.find(m => m.user_id !== userId);
          return other ? { id: other.user_id } : null;
        })();

        if (socket && peer?.id) {
          socket.emit('presence query', { userId: peer.id });
        }
      } catch (err) {
        console.error('Failed to load chat:', err);
      }
    };

    // Reset pagination state when switching chats
    historyCountRef.current = 0;
    setHasMore(true);
    setLoadingMore(false);
    fetchChat();
  }, [chatId]);

  // Load older messages when user scrolls near the top
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const onScroll = async () => {
      if (!hasMore || loadingMore) return;
      if (el.scrollTop <= 80) {
        setLoadingMore(true);
        const prevScrollHeight = el.scrollHeight;
        const prevTop = el.scrollTop;
        try {
          const offset = historyCountRef.current;
          const res = await axiosInstance.get(`/chat/get_chat/${chatId}?limit=${PAGE_SIZE}&offset=${offset}`);
          const older = res.data.messages || [];
          if (older.length > 0) {
            // Prepend unique older messages while preserving scroll position
            setMessages(prev => {
              const seen = new Set(prev.map(m => m.message_id));
              const uniqueOlder = older.filter(m => !seen.has(m.message_id));
              const next = [...uniqueOlder, ...prev];
              // After DOM updates, adjust scroll so the viewport stays anchored
              requestAnimationFrame(() => {
                const newScrollHeight = el.scrollHeight;
                el.scrollTop = prevTop + (newScrollHeight - prevScrollHeight);
              });
              return next;
            });
            historyCountRef.current += older.length;
            if (older.length < PAGE_SIZE) setHasMore(false);
          } else {
            setHasMore(false);
          }
        } catch (e) {
          console.error('Failed to load older messages:', e);
        } finally {
          setLoadingMore(false);
        }
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [chatId, hasMore, loadingMore]);

  useEffect(() => {
    const peer = getPeerUser();
    if (socket && peer?.id) {
      socket.emit('presence query', { userId: peer.id });
    }
  }, [socket, chatInfo]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedEmoji = e.target.closest('.emoji-picker-wrapper');
      const clickedMenu = e.target.closest('.message-actions-menu');
      if (!clickedEmoji && !clickedMenu) {
        setShowPickerFor(null);
        setActionFor(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showPickerFor === null && pickerObserverRef.current) {
      try { pickerObserverRef.current.disconnect(); } catch {}
      pickerObserverRef.current = null;
      setPickerExpanded(false);
    }
  }, [showPickerFor]);

  // If the user cannot interact (left the group), ensure any open overlays are closed
  useEffect(() => {
    if (hasLeftGroup) {
      setShowPickerFor(null);
      setActionFor(null);
    }
  }, [hasLeftGroup]);

  const attachPickerObserver = (el) => {
    if (!el) return;
    const root = el.querySelector('.EmojiPickerReact');
    if (!root) return;

    if (pickerObserverRef.current) {
      try { pickerObserverRef.current.disconnect(); } catch {}
      pickerObserverRef.current = null;
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect?.height || 0;
        setPickerExpanded(h > 80);
      }
    });
    ro.observe(root);
    pickerObserverRef.current = ro;
  };

  useEffect(() => {
    if (messages.length > 0 && !hasMountedOnce.current) {
      scrollToBottom(false);
      hasMountedOnce.current = true;
    }
  }, [messages]);

  const getPeerUser = () => {
    if (chatInfo?.user_id) {
      return {
        id: chatInfo.user_id,
        name: chatInfo?.chat_name || '',
        image: chatInfo?.profile_url || null,
      };
    }
    const other = chatInfo?.members?.find(m => m.user_id !== userId);
    if (other) {
      return {
        id: other.user_id,
        name: other.username || '',
        image: other.profile_url || null,
      };
    }
    return null;
  };

  const handleStartAudioCall = () => {
    const peer = getPeerUser();
    if (!peer) return;
    navigate(`/call/${peer.id}`, { state: { peer, video: false, chatId } });
  };

  const handleStartVideoCall = () => {
    const peer = getPeerUser();
    if (!peer) return;
    navigate(`/call/${peer.id}`, { state: { peer, video: true, chatId } });
  };

  const openNativeCamera = async () => {
    try {
      const result = await CameraPlugin.openCamera();

      if (!result || !result.fileUri) {
        console.error('No fileUri received from native camera.');
        return;
      }

      if (result.isVideo) {
        navigate('/see-content-chat', { state: { video: result.fileUri, chatId, userId, replyTo } });
      }
      else {
        navigate('/see-content-chat', { state: { photo: result.fileUri, chatId, userId, replyTo } });
      }
    } catch (error) {
      console.error('Error opening native camera:', error);
    }
  };

  const requestGalleryPermissions = async () => {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        console.log('iOS detected - no permissions needed for FilePicker');
        return true;
      }

      if (Capacitor.getPlatform() === 'web') {
        console.log('Web detected - no permissions needed for FilePicker');
        return true;
      }

      if (Capacitor.getPlatform() === 'android') {
        console.log('Android detected - attempting to request FilePicker permissions...');
        
        try {
          const requestResult = await FilePicker.requestPermissions();
          console.log('Permission request result:', requestResult);
          
          const readGranted = requestResult.readExternalStorage === 'granted';
          const mediaGranted = requestResult.accessMediaLocation === 'granted' || 
                               requestResult.accessMediaLocation === undefined || 
                               requestResult.accessMediaLocation === 'not-required';
          
          console.log('Read external storage granted:', readGranted);
          console.log('Access media location:', requestResult.accessMediaLocation);
          
          
          const hasPermission = readGranted || requestResult.readExternalStorage === 'denied';
          
          console.log('Overall permission decision:', hasPermission);
          return hasPermission;
          
        } catch (permError) {
          console.log('Permission request failed, but this might be normal on modern Android:', permError);
          return true;
        }
      }

      return true;
    } catch (error) {
      console.error('Error in permission flow:', error);
      console.log('Permission check failed, but proceeding anyway...');
      return true;
    }
  };

  const handlePickFile = async () => {
    try {
      console.log('Starting file picker process...');
      
      const hasPermission = await requestGalleryPermissions();
      
      if (!hasPermission) {
        console.log('Gallery permissions not granted, but attempting to proceed anyway...');
      }

      console.log('Attempting to pick files...');
      const result = await FilePicker.pickFiles({ readData: true, limit: 1 });
      console.log('FilePicker result:', result);

      if (!result.files || result.files.length === 0) {
        console.log('No files selected or user cancelled.');
        return;
      }
      
      if (result.files.length > 1) {
        console.log('More than one file selected, using first one.');
      }

      const picked = result.files[0];
      const { data, name, mimeType, size } = picked;
      
      console.log('Selected file:', { name, mimeType, size });

      const maxSizeBytes = 50 * 1024 * 1024;
      if (size > maxSizeBytes) {
        console.error('File too large, skipping:', name);
        return;
      }

      const blob = base64ToBlob(data, mimeType);
      const file = new File([blob], name, { type: mimeType });

      let currentChatId = chatId;
      if (!currentChatId) {
        const res = await axiosInstance.post('/chat/create_chat', {
          user_id: chatInfo?.user_id,
        });
        currentChatId = res.data.chat_id;
        socket.emit('join room', currentChatId);

        navigate(`/chat/${currentChatId}`, {
          replace: true,
          state: {
            ...(location.state || {}),
            chatInfo,
            preserveMessages: true,
          },
        });
      }

      let messageType = 'file';
      if (mimeType?.startsWith('image/')) messageType = 'photo';
      else if (mimeType?.startsWith('video/')) messageType = 'video';
      else if (mimeType?.startsWith('audio/')) messageType = 'audio';

      // Create optimistic pending message with local preview
      const tempId = createTempId();
      const localUrl = URL.createObjectURL(file);
      const optimistic = {
        message_id: tempId,
        chat_id: currentChatId || 'pending-chat',
        sender: userId,
        content: localUrl,
        type: messageType,
        is_reply_to: replyTo ? replyTo.message_id : null,
        is_sent_by_you: true,
        sender_username: '',
        sender_profile_url: null,
        pending: true,
        statuses: [],
        ...(messageType === 'file' ? { file_name: name } : {}),
      };
      setMessages(prev => [...prev, optimistic]);
      setReplyTo(null);
      scrollToBottom();

      console.log('Requesting signed URL for upload...');
      const uploadData = await axiosInstance.post('/chat/signed-url', {
        name: name || `media_${Date.now()}`,
        type: mimeType,
        messageType,
      });

      const { uploadUrl, messageId, filename } = uploadData.data;

      // Store finalContent for reconciliation
      setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, finalContent: filename } : m));

      console.log('Uploading file...');
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });

      console.log('File uploaded successfully, sending message...');
      const msg = {
        id: messageId,
        chat_id: currentChatId,
        sender: userId,
        content: filename,
        type: messageType,
        is_reply_to: replyTo ? replyTo.message_id : null,
        ...(messageType === 'file' ? { file_name: name } : {}),
      };
      socket.emit('chat message', msg);
      // replyTo already cleared when adding optimistic
      
      console.log('Message sent successfully');
    } catch (err) {
      if (err.message !== 'User canceled') {
        console.error('Error selecting media:', err);
        console.error('Full error details:', JSON.stringify(err, Object.getOwnPropertyNames(err), 2));
      } else {
        console.log('User cancelled file selection');
      }
      // Mark last optimistic (if any) as failed
      setMessages(prev => {
        const last = [...prev].reverse().find(m => m.pending);
        if (!last) return prev;
        return prev.map(m => m.message_id === last.message_id ? { ...m, pending: false, failed: true } : m);
      });
    }
  };

  const handlePickFromGallery = async () => {
    try {
      // iOS: use the native plugin. Android/web: use <input type="file"> which opens the OS picker.
      const platform = Capacitor.getPlatform();
      if (Capacitor.isNativePlatform() && platform === 'ios' && CameraPlugin?.openGallery) {
        const result = await CameraPlugin.openGallery();
        if (!result || !result.fileUri) return;

        const isVideo = !!result.isVideo;
        const uri = result.fileUri;

        const fetchable = uri.startsWith('data:') ? uri : Capacitor.convertFileSrc(uri);
        const resp = await fetch(fetchable);
        const blob = await resp.blob();

        const mimeType = blob.type || (isVideo ? 'video/mp4' : 'image/jpeg');
        const ext = isVideo ? 'mp4' : 'jpg';
        const name = `${isVideo ? 'video' : 'photo'}_${Date.now()}.${ext}`;
        const file = new File([blob], name, { type: mimeType });

        let currentChatId = chatId;
        if (!currentChatId) {
          const res = await axiosInstance.post('/chat/create_chat', {
            user_id: chatInfo?.user_id,
          });
          currentChatId = res.data.chat_id;
          socket.emit('join room', currentChatId);

          navigate(`/chat/${currentChatId}`, {
            replace: true,
            state: {
              ...(location.state || {}),
              chatInfo,
              preserveMessages: true,
            },
          });
        }

        const messageType = isVideo ? 'video' : 'photo';
        // Create optimistic pending message with local preview
        const tempId = createTempId();
        const localUrl = URL.createObjectURL(file);
        const optimistic = {
          message_id: tempId,
          chat_id: currentChatId || 'pending-chat',
          sender: userId,
          content: localUrl,
          type: messageType,
          is_reply_to: replyTo ? replyTo.message_id : null,
          is_sent_by_you: true,
          sender_username: '',
          sender_profile_url: null,
          pending: true,
          statuses: [],
        };
        setMessages(prev => [...prev, optimistic]);
        setReplyTo(null);
        scrollToBottom();
        const uploadData = await axiosInstance.post('/chat/signed-url', {
          name,
          type: mimeType,
          messageType,
        });

        const { uploadUrl, messageId, filename } = uploadData.data;

        // Store finalContent for reconciliation
        setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, finalContent: filename } : m));

        await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mimeType },
          body: file,
        });

        const msg = {
          id: messageId,
          chat_id: currentChatId,
          sender: userId,
          content: filename,
          type: messageType,
          is_reply_to: replyTo ? replyTo.message_id : null,
        };
        socket.emit('chat message', msg);
        // replyTo already cleared when adding optimistic
        return;
      }
      // Android/web/etc.
      galleryInputRef.current?.click?.();
    } catch (err) {
      const message = err?.message || '';
      if (!/cancel|User cancelled/i.test(message)) {
        console.error('Error picking from gallery:', err);
      }
      // Mark last optimistic (if any) as failed
      setMessages(prev => {
        const last = [...prev].reverse().find(m => m.pending);
        if (!last) return prev;
        return prev.map(m => m.message_id === last.message_id ? { ...m, pending: false, failed: true } : m);
      });
    }
  };

  const handleGalleryInputChange = async (e) => {
    const picked = e?.target?.files?.[0];
    // Allow selecting the same file again later
    try { e.target.value = ''; } catch {}
    if (!picked) return;

    const tempId = createTempId();
    try {
      const maxSizeBytes = 50 * 1024 * 1024;
      if (picked.size > maxSizeBytes) {
        console.error('File too large, skipping:', picked.name);
        return;
      }

      const mimeType = picked.type || 'image/jpeg';
      const messageType = mimeType.startsWith('video/') ? 'video' : 'photo';
      const name = picked.name || `${messageType}_${Date.now()}`;

      let currentChatId = chatId;
      if (!currentChatId) {
        const res = await axiosInstance.post('/chat/create_chat', {
          user_id: chatInfo?.user_id,
        });
        currentChatId = res.data.chat_id;
        socket.emit('join room', currentChatId);

        navigate(`/chat/${currentChatId}`, {
          replace: true,
          state: {
            ...(location.state || {}),
            chatInfo,
            preserveMessages: true,
          },
        });
      }

      // Create optimistic pending message with local preview
      const localUrl = URL.createObjectURL(picked);
      const optimistic = {
        message_id: tempId,
        chat_id: currentChatId || 'pending-chat',
        sender: userId,
        content: localUrl,
        type: messageType,
        is_reply_to: replyTo ? replyTo.message_id : null,
        is_sent_by_you: true,
        sender_username: '',
        sender_profile_url: null,
        pending: true,
        statuses: [],
      };
      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);
      scrollToBottom();

      const uploadData = await axiosInstance.post('/chat/signed-url', {
        name,
        type: mimeType,
        messageType,
      });

      const { uploadUrl, messageId, filename } = uploadData.data;

      // Store finalContent for reconciliation
      setMessages((prev) =>
        prev.map((m) => (m.message_id === tempId ? { ...m, finalContent: filename } : m))
      );

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: picked,
      });

      const msg = {
        id: messageId,
        chat_id: currentChatId,
        sender: userId,
        content: filename,
        type: messageType,
        is_reply_to: replyTo ? replyTo.message_id : null,
      };
      socket.emit('chat message', msg);
    } catch (err) {
      console.error('Error uploading selected media:', err);
      setMessages((prev) =>
        prev.map((m) =>
          m.message_id === tempId ? { ...m, pending: false, failed: true } : m
        )
      );
    }
  };

  const startRecording = async (e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      try { if (e?.currentTarget?.blur) e.currentTarget.blur(); } catch {}
      try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch {}
      try { const sel = window.getSelection?.(); if (sel && sel.removeAllRanges) sel.removeAllRanges(); } catch {}

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let options = {};
      if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        options = { mimeType: 'audio/webm; codecs=opus' };
      }
      else if (MediaRecorder.isTypeSupported('audio/mp4; codecs=aac')) {
        options = { mimeType: 'audio/mp4; codecs=aac' };
      }
      const recorder = new MediaRecorder(stream, options);

      chunksRef.current = [];
      mediaRecorderRef.current = recorder;

      willDiscardRef.current = false;
      discardRef.current = false;
      setShowTrash(false);

      recorder.ondataavailable = (ev) => chunksRef.current.push(ev.data);

      recorder.onstop = async () => {
        const shouldDiscard = discardRef.current;

        setIsRecording(false);
        setRecordingHint("");
        setShowTrash(false);
        willDiscardRef.current = false;

        if (shouldDiscard) {
          discardRef.current = false;
          return;
        }
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const file = new File([blob], 'audio_message.webm', { type: 'audio/webm' });

          // Optimistic pending audio message with local preview
          const tempId = createTempId();
          const localUrl = URL.createObjectURL(blob);
          const optimistic = {
            message_id: tempId,
            chat_id: chatId || 'pending-chat',
            sender: userId,
            content: localUrl,
            type: 'audio',
            is_reply_to: replyTo ? replyTo.message_id : null,
            is_sent_by_you: true,
            sender_username: '',
            sender_profile_url: null,
            pending: true,
            statuses: [],
          };
          setMessages(prev => [...prev, optimistic]);
          setReplyTo(null);
          scrollToBottom();

          const response = await axiosInstance.post('/chat/signed-url', {
            name: file.name,
            type: file.type,
            messageType: 'audio',
          });

          const { uploadUrl, messageId, filename } = response.data;

          // Store finalContent for reconciliation
          setMessages(prev => prev.map(m => m.message_id === tempId ? { ...m, finalContent: filename } : m));

          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });

          // Ensure chat exists if needed before emitting
          let currentChatId = chatId;
          if (!currentChatId) {
            const res = await axiosInstance.post('/chat/create_chat', {
              user_id: chatInfo?.user_id,
            });
            currentChatId = res.data.chat_id;
            socket.emit('join room', currentChatId);

            navigate(`/chat/${currentChatId}`, {
              replace: true,
              state: {
                ...(location.state || {}),
                chatInfo,
                preserveMessages: true,
              },
            });
          }

          const msg = {
            id: messageId,
            chat_id: currentChatId,
            sender: userId,
            content: filename,
            type: 'audio',
            is_reply_to: replyTo ? replyTo.message_id : null,
          };
          socket.emit('chat message', msg);
        } catch (err) {
          console.error('Error preparing/sending audio message:', err);
          // Mark last optimistic (if any) as failed
          setMessages(prev => {
            const last = [...prev].reverse().find(m => m.pending);
            if (!last) return prev;
            return prev.map(m => m.message_id === last.message_id ? { ...m, pending: false, failed: true } : m);
          });
        }
      };

  recorder.start();
  hasStartedRecordingRef.current = true;
      setIsRecording(true);
      setRecordingHint("Recording...");

      stopTimerRef.current = setTimeout(() => stopRecording({ discard: false }), 60000);

      const handleMove = (ev) => {
        if (!micButtonRef.current) return;
        const rect = micButtonRef.current.getBoundingClientRect();
        const inside =
          ev.clientX >= rect.left &&
          ev.clientX <= rect.right &&
          ev.clientY >= rect.top &&
          ev.clientY <= rect.bottom;

        willDiscardRef.current = !inside;
        setRecordingHint(inside ? "Recording..." : "Release to discard");
        setShowTrash(!inside);
        setDragPos({ x: ev.clientX, y: ev.clientY });
      };

      document.addEventListener("pointermove", handleMove);

      const handleUpLike = () => {
        document.removeEventListener("pointermove", handleMove);
        const discard = willDiscardRef.current;
        setShowTrash(false);
        stopRecording({ discard });
      };

      document.addEventListener("pointerup", handleUpLike, { once: true });
      document.addEventListener("pointercancel", handleUpLike, { once: true });
    } catch (err) {
      console.error('Mic access denied or error:', err);
    }
  };

  const handleMicPointerDown = (e) => {
    try { e.preventDefault?.(); } catch {}
    try { e.currentTarget?.blur?.(); } catch {}
    // start recording only after long-press threshold
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      startRecording(e);
    }, HOLD_TO_RECORD_MS);
  };

  const handleMicPointerUp = () => {
    // If recording hasn't started yet, cancel the hold timer
    if (!hasStartedRecordingRef.current && holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    // If already recording, the startRecording listener will handle pointerup
  };

  const stopRecording = ({ discard = false } = {}) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      discardRef.current = discard;
      if (discard) setRecordingHint('discard');
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
      clearTimeout(stopTimerRef.current);
      hasStartedRecordingRef.current = false;
    }
  };

  // Auto-resize message composer (textarea)
  const autoResizeComposer = () => {
    const el = composerRef.current;
    if (!el) return;
    // If empty: remove explicit height and hide scrollbar
    if (!el.value) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      return;
    }
    // Determine cap from inline style or computed styles (fallback 160px)
    const maxHStr = el.style.maxHeight || window.getComputedStyle(el).maxHeight;
    const cap = parseInt(maxHStr, 10) || 160;
    // Reset to shrink if needed, then grow to content height up to cap
    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, cap);
    el.style.height = `${next}px`;
    // Toggle scrollbar only when content exceeds cap
    el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
  };

  useEffect(() => {
    // Initialize height when component mounts or when value clears
    autoResizeComposer();
  }, [newMessage]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const text = newMessage.trim();
    setNewMessage('');

    // Create optimistic message
    const tempId = createTempId();
    const optimistic = {
      message_id: tempId,
      chat_id: chatId || 'pending-chat',
      sender: userId,
      content: text,
      type: undefined,
      is_reply_to: replyTo ? replyTo.message_id : null,
      is_sent_by_you: true,
      sender_username: '',
      sender_profile_url: null,
      pending: true,
      statuses: [],
    };
    setMessages((prev) => [...prev, optimistic]);
    setReplyTo(null);
    scrollToBottom();

    try {
      let currentChatId = chatId;
      if (!currentChatId) {
        if (!chatInfo?.user_id) {
          throw new Error('Missing peer user id; cannot create chat');
        }
        const res = await axiosInstance.post('/chat/create_chat', {
          user_id: chatInfo?.user_id,
        });
        currentChatId = res.data.chat_id;
        socket.emit('join room', currentChatId);

        // Update URL so the rest of the chat interactions use a real chatId.
        navigate(`/chat/${currentChatId}`, {
          replace: true,
          state: {
            ...(location.state || {}),
            chatInfo,
            preserveMessages: true,
          },
        });

        // Ensure the optimistic message points to the real chat.
        setMessages((prev) =>
          prev.map((m) => (m.message_id === tempId ? { ...m, chat_id: currentChatId } : m))
        );
      }

      const msg = {
        chat_id: currentChatId,
        sender: userId,
        content: text,
        is_reply_to: optimistic.is_reply_to,
      };
      socket.emit('chat message', msg);
    } catch (e) {
      // If sending fails, mark the optimistic message as failed
      setMessages((prev) =>
        prev.map((m) =>
          m.message_id === tempId ? { ...m, pending: false, failed: true } : m
        )
      );
    }
  };

  const handleReaction = (emojiData, messageId) => {
    if (!canInteract) return;
    const reaction = { chat_id: chatId, user: userId, emoji: emojiData.emoji };
    socket.emit('reaction', { messageId, reaction });
    setShowPickerFor(null);
    setActionFor(null);
  };

  const closeOverlays = () => {
    setShowPickerFor(null);
    setActionFor(null);
  };

  const handleReplyFromMenu = (msg) => {
    if (!canInteract) return;
    setReplyTo(msg);
    closeOverlays();
  };

  const handleCopy = async (msg) => {
    try {
      const textToCopy = !msg.type ? msg.content : (msg.content || '');
      if (textToCopy) {
        await navigator.clipboard.writeText(textToCopy);
      }
    } catch (err) {
      console.error('Copy failed:', err);
    } finally {
      closeOverlays();
    }
  };

  const handleUnsend = (msg) => {
    if (!msg.is_sent_by_you) return;
    try {
      socket.emit('unsend', { chatId, messageId: msg.message_id, userId });
    } catch (err) {
      console.error('Unsend failed:', err);
    } finally {
      closeOverlays();
    }
  };

  const groupReactions = (reactions) => {
    const map = new Map();

    reactions.forEach((reaction) => {
      map.set(reaction.emoji, (map.get(reaction.emoji) || 0) + 1);
    });

    return Array.from(map.entries()).map(([emoji, count]) => ({
      emoji,
      count,
    }));
  };

  let pressTimer;
  let startY;

  const handlePointerDown = (id, e) => {
    if (hasLeftGroup) return;
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    startY = e.clientY;
    pressTimer = setTimeout(() => {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
      setShowPickerFor(id);
      setActionFor(id);
    }, 1000);
  };

  const handlePointerMove = (e) => {
    if (startY && Math.abs(e.clientY - startY) > 10) {
      clearTimeout(pressTimer);
    }
  };

  const handlePointerUp = () => {
    clearTimeout(pressTimer);
    startY = null;
  };

  return (
    <div className="tailwind">
      <motion.div
        initial={{ opacity: 0, x: fromSettings ? -100 : 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: fromSettings ? 100 : -50 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="flex flex-col h-screen bg-white dark:bg-darkBackground text-black dark:text-white transition-colors"
      >
        <ChatHeader
          chatId={chatId}
          chatInfo={chatInfo}
          peerPresence={peerPresence}
          fromSettings={fromSettings}
          onBack={() => navigate('/chat/conversations')}
          onOpenSettings={(id, info) => {
            if (hasLeftGroup) return;
            navigate(`/chat/${id}/settings`, { state: { chatInfo: info } });
          }}
          onStartAudioCall={handleStartAudioCall}
          onStartVideoCall={handleStartVideoCall}
        />

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleGalleryInputChange}
          style={{ display: 'none' }}
        />

        {isLoading ? (
          <div className="flex-1 p-4 space-y-3">
            <div className="animate-pulse space-y-3">
              <div className="w-2/3 h-12 bg-gray-200 dark:bg-neutral-700 rounded-md" />
              <div className="w-1/2 h-10 bg-gray-200 dark:bg-neutral-700 rounded-md ml-auto" />
              <div className="w-5/6 h-14 bg-gray-200 dark:bg-neutral-700 rounded-md" />
              <div className="w-2/3 h-10 bg-gray-200 dark:bg-neutral-700 rounded-md" />
              <div className="w-1/2 h-16 bg-gray-200 dark:bg-neutral-700 rounded-md ml-auto" />
              <div className="w-5/6 h-10 bg-gray-200 dark:bg-neutral-700 rounded-md" />
            </div>
          </div>
        ) : (
          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide p-4 pb-7 space-y-2"
          >
            {loadingMore && hasMore && (
              <div className="sticky top-0 z-10 flex justify-center py-2">
                <CircularProgress size={18} thickness={5} />
              </div>
            )}
            {messages.map((msg, index) => {
              const thisRef = messageRefs.current.get(msg.message_id) || React.createRef();
              messageRefs.current.set(msg.message_id, thisRef);
              const seenBy = chatInfo?.members
                ?.filter(m => m.user_id !== userId)
                .filter(member => {
                  const lastSeenMessage = [...messages]
                    .reverse()
                    .find(m2 =>
                      (m2.statuses || []).some(
                        s => s.user_id === member.user_id && s.status === 'seen'
                      )
                    );
                  return lastSeenMessage?.message_id === msg.message_id;
                });

              const prevMsg = messages[index - 1];
              const isSameSenderAsPrev = prevMsg && prevMsg.sender_username === msg.sender_username;
              const isFirstInBlock = !isSameSenderAsPrev;

              const nextMsg = messages[index + 1];
              const isSameSenderAsNext = nextMsg && nextMsg.sender_username === msg.sender_username;
              const isLastInBlock = !isSameSenderAsNext;

              const lastIncoming = [...messages].reverse().find(m => !m.is_sent_by_you);

              const profileUrl = chatInfo?.is_group
                ? msg.sender_profile_url
                : chatInfo?.profile_url;

              return (
                <div
                  key={msg.message_id}
                  className={`relative w-full flex ${msg.is_sent_by_you ? 'justify-end' : 'justify-start'} ${seenBy?.length > 0 ? 'mb-[2.5rem_!important]' : ''} ${
                    (msg.reactions && msg.reactions.length > 0)
                      ? (msg.type === 'photo'
                          ? 'mb-[1.5rem_!important]'
                          : 'mb-[2rem_!important]')
                      : ''
                  }`}
                >
                  <motion.div
                    ref={thisRef}
                    initial={false}
                    animate={
                      highlightedMessageId === msg.message_id
                        ? { scale: 1.1 }
                        : { scale: 1 }
                    }
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className={`flex flex-col w-fit ${msg.is_sent_by_you ? 'items-end ml-auto' : 'items-start mr-auto'}`}
                  >
                    {!msg.is_sent_by_you && msg.message_id === lastIncoming?.message_id && (
                      <SeenObserver chatId={chatId} messageId={msg.message_id} userId={userId} messageRefs={messageRefs} socket={socket}/>
                    )}

                    {chatInfo?.is_group && !msg.is_sent_by_you && msg.sender_username && isFirstInBlock && (
                      <span className="w-full text-right text-xs text-gray-400 dark:text-gray-300 mb-1 pr-2">
                        {msg.sender_username || ''}
                      </span>
                    )}

                    {msg.is_reply_to && (
                      <div
                        onClick={() => {
                          const repliedToRef = messageRefs.current.get(msg.is_reply_to);
                          if (repliedToRef?.current) {
                            repliedToRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            setHighlightedMessageId(msg.is_reply_to);

                            setTimeout(() => {
                              setHighlightedMessageId(null);
                            }, 800);
                          }
                        }}
                        className="ml-7 mb-1 px-2 py-2 rounded-md bg-chatGrayColor text-xs text-gray-600 dark:text-gray-300 w-fit max-w-[16rem]"
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          <Undo2 className="w-4 h-4 text-gray-400 dark:text-gray-300 flex-shrink-0" />
                          <span>Replied to</span>
                        </div>
                        <div className="text-black dark:text-white font-medium truncate max-w-[12rem]">
                          {(() => {
                            const original = messages.find(m => m.message_id === msg.is_reply_to);
                            if (!original) return '';

                            if (original.type === 'audio') return 'a voice message';
                            if (original.type === 'photo') return 'a photo';
                            if (original.type === 'video') return 'a video';
                            if (original.type === 'file') return 'a file';

                            return original.content;
                          })()}
                        </div>
                      </div>
                    )}

                    <div className={`flex items-end gap-x-2 ${msg.is_sent_by_you ? 'flex-row-reverse' : 'flex-row'}`}>
                      {!msg.is_sent_by_you ? 
                        isLastInBlock ? (
                          <img
                            src={profileUrl || '/assets/images/default_profile.png'}
                            alt={msg.sender_username || chatInfo?.chat_name}
                            className="w-7 h-7 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-7" />
                        ) 
                      : ''}

                      <div className="flex flex-col items-end max-w-[16rem]">
                        <SwipeMessage
                          key={msg.message_id}
                          msg={msg}
                          onReply={msg.type !== 'deleted' && canInteract ? setReplyTo : undefined}
                          onPointerDown={msg.type !== 'deleted' && canInteract ? (e) => handlePointerDown(msg.message_id, e) : undefined}
                          onPointerUp={msg.type !== 'deleted' && canInteract ? handlePointerUp : undefined}
                          onPointerMove={msg.type !== 'deleted' && canInteract ? (e) => handlePointerMove(e) : undefined}
                          elevated={showPickerFor === msg.message_id || actionFor === msg.message_id}
                          className={`relative w-fit max-w-[16rem] px-4 py-2 rounded-2xl ${
                            msg.type === 'audio' ? (
                              msg.is_sent_by_you
                                ? '!-m-4 self-end ml-auto'
                                : '!-m-4 self-start mr-auto'
                            ) : msg.type === 'photo' || msg.type === 'video' ? (
                              msg.is_sent_by_you
                                ? 'self-end ml-auto'
                                : 'self-start mr-auto'
                            ) : (
                              msg.is_sent_by_you
                                ? 'bg-chatBlueColor text-white self-end ml-auto'
                                : 'bg-chatGrayColor self-start mr-auto text-black dark:text-white'
                            )
                          } ${actionFor === msg.message_id ? 'z-50' : ''}`}
                        >
                          {msg.type !== 'deleted' && showPickerFor === msg.message_id && (
                            <div
                              ref={attachPickerObserver}
                              className={`emoji-picker-wrapper emoji-picker-theme absolute ${
                                pickerExpanded ? 'top-full mt-2' : 'bottom-full mb-2'
                              } z-50 ${
                                msg.is_sent_by_you
                                  ? msg.type === 'audio' ? 'right-4' : 'right-0'
                                  : msg.type === 'audio' ? 'left-4' : 'left-0'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Picker
                                theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                                reactionsDefaultOpen={true}
                                searchDisabled={true}
                                autoFocusSearch={false}
                                onReactionClick={(e) => handleReaction(e, msg.message_id)}
                              />
                            </div>
                          )}

                          {msg.type !== 'deleted' && actionFor === msg.message_id && (
                            <div className={`message-actions-menu absolute top-full mt-2 ${showPickerFor === msg.message_id ? 'z-40' : 'z-50'} ${msg.is_sent_by_you ? (msg.type === 'audio' ? 'right-4' : 'right-0') : (msg.type === 'audio' ? 'left-4' : 'left-0')}`}>
                              <div className="flex flex-col bg-white dark:bg-darkCard shadow-lg rounded-lg py-2 min-w-[8rem]">
                                <button
                                  className="flex items-center gap-2 text-sm text-black dark:text-white px-4 py-2"
                                  onClick={() => handleReplyFromMenu(msg)}
                                >
                                  <Undo2 className="w-4 h-4 text-black dark:text-white" />
                                  Reply
                                </button>
                                {!msg.type && (
                                  <button
                                    className="flex items-center gap-2 text-sm text-black dark:text-white px-4 py-2"
                                    onClick={() => handleCopy(msg)}
                                  >
                                    <Copy className="w-4 h-4 text-black dark:text-white" />
                                    Copy
                                  </button>
                                )}
                                {msg.is_sent_by_you && (
                                  <button
                                    className="flex items-center gap-2 text-sm text-red-600 px-4 py-2"
                                    onClick={() => handleUnsend(msg)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Unsend
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        
                          {!msg.type ? (
                            <span className="break-words whitespace-pre-wrap">{msg.content}</span>
                          ) : msg.type === 'deleted' ? (
                            <span className="italic">This message was deleted</span>
                          ) : msg.type === 'audio' ? (
                            <AudioMessage src={msg.content} isSentByYou={msg.is_sent_by_you} />
                          ) : msg.type === 'photo' ? (
                            <img
                              src={msg.content}
                              alt="Sent photo"
                              className="max-w-[12rem] rounded-lg object-cover"
                              onClick={() => setOpenMedia({ type: 'photo', src: msg.content })}
                              //onLoad={scrollToBottom(false)}
                            />
                          ) : msg.type === 'video' ? (
                            <div onClick={() => setOpenMedia({ type: 'video', src: msg.content })}>
                              <video
                                src={msg.content}
                                className="max-w-[12rem] rounded-lg"
                              />
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">                                
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-14 h-14 text-white drop-shadow-lg"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.35)" />
                                  <polygon points="10,8 16,12 10,16" fill="white" />
                                </svg>
                              </div>
                            </div>
                          ) : msg.type === 'file' ? (
                            <a
                              href={msg.content}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={msg.file_name || undefined}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                msg.is_sent_by_you ? 'bg-chatBlueColor text-white' : 'bg-chatGrayColor text-gray-900 dark:text-gray-100'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FileText className="w-5 h-5" />
                              <span className="truncate max-w-[10rem]">
                                {msg.file_name || 'File'}
                              </span>
                            </a>
                          ) : msg.type === 'audio call' ? (
                            <span
                              className="flex flex-row items-center gap-3 mx-auto text-base rounded-md px-3 py-2"
                              onClick={handleStartAudioCall}
                            >
                              <span>
                                {msg.call_status === "missed" ? (
                                  <span className="w-9 h-9 bg-red-600 rounded-full flex items-center justify-center">
                                    <HiPhoneXMark className="w-6 h-6 text-white bg-red-600 rounded-full p-0.5" />
                                  </span>
                                ) : msg.is_sent_by_you ? (
                                  <HiPhoneArrowUpRight className="w-6 h-6" />
                                ) : (
                                  <HiPhoneArrowDownLeft className="w-6 h-6" />
                                )}
                              </span>
                              <span className="flex flex-col items-start">
                                <span className="font-bold">
                                  {msg.call_status === "missed"
                                    ? "Missed audio call"
                                    : "Audio call"}
                                </span>
                                {msg.call_status === "ended" && typeof msg.call_duration === "number" ? (
                                  <span className={`text-xs ${msg.is_sent_by_you ? 'text-gray-100' : 'text-gray-300'}`}>
                                    {(() => {
                                      const hrs = Math.floor(msg.call_duration / 3600);
                                      const mins = Math.floor((msg.call_duration % 3600) / 60);
                                      const secs = msg.call_duration % 60;
                                      const parts = [];
                                      if (hrs > 0) parts.push(`${hrs}h`);
                                      if (mins > 0) parts.push(`${mins} mins`);
                                      if (secs > 0 || parts.length === 0) parts.push(`${secs} seconds`);
                                      return parts.join(" ");
                                    })()}
                                  </span>
                                ) : msg.call_status === "missed" ? (
                                  <span className={`text-xs ${msg.is_sent_by_you ? 'text-gray-100' : 'text-gray-300'}`}>Tap to call {msg.is_sent_by_you ? 'again' : 'back'}</span>
                                ) : null}
                              </span>
                            </span>
                          ) : msg.type === 'video call' ? (
                            <span
                              className="flex flex-row items-center gap-3 mx-auto text-base rounded-md px-3 py-2"
                              onClick={handleStartVideoCall}
                            >
                              <span>
                                {msg.call_status === "missed" ? (
                                  <span className="w-9 h-9 bg-red-600 rounded-full flex items-center justify-center">
                                    <HiPhoneXMark className="w-6 h-6 text-white bg-red-600 rounded-full p-0.5" />
                                  </span>
                                ) : msg.is_sent_by_you ? (
                                  <HiPhoneArrowUpRight className="w-6 h-6" />
                                ) : (
                                  <HiPhoneArrowDownLeft className="w-6 h-6" />
                                )}
                              </span>
                              <span className="flex flex-col items-start">
                                <span className="font-bold">
                                  {msg.call_status === "missed"
                                    ? "Missed video call"
                                    : "Video call"}
                                </span>
                                {msg.call_status === "ended" && typeof msg.call_duration === "number" ? (
                                  <span className={`text-xs ${msg.is_sent_by_you ? 'text-gray-100' : 'text-gray-300'}`}>
                                    {(() => {
                                      const hrs = Math.floor(msg.call_duration / 3600);
                                      const mins = Math.floor((msg.call_duration % 3600) / 60);
                                      const secs = msg.call_duration % 60;
                                      const parts = [];
                                      if (hrs > 0) parts.push(`${hrs}h`);
                                      if (mins > 0) parts.push(`${mins} mins`);
                                      if (secs > 0 || parts.length === 0) parts.push(`${secs} seconds`);
                                      return parts.join(" ");
                                    })()}
                                  </span>
                                ) : msg.call_status === "missed" ? (
                                  <span className={`text-xs ${msg.is_sent_by_you ? 'text-gray-100' : 'text-gray-300'}`}>Tap to call {msg.is_sent_by_you ? 'again' : 'back'}</span>
                                ) : null}
                              </span>
                            </span>
                          ) : (
                            <span className="break-words whitespace-pre-wrap">{msg.content}</span>
                          )}

                          {msg.type !== 'deleted' && msg.reactions && msg.reactions.length > 0 && (
                            <div
                              className={`absolute ${
                                msg.type === 'photo'
                                  ? '-bottom-3'
                                  : msg.type === 'audio'
                                  ? '-bottom-2'
                                  : '-bottom-6'
                              } ${
                                msg.type === 'audio'
                                  ? msg.is_sent_by_you
                                    ? 'left-6'
                                    : 'right-6'
                                  : msg.is_sent_by_you
                                  ? 'left-0'
                                  : 'right-0'
                              } flex gap-1`}
                            >
                              {msg.reactions && msg.reactions.length > 0 && (
                                <div
                                  className={`absolute bottom-0 ${
                                    msg.is_sent_by_you ? 'left-0' : 'right-0'
                                  } flex gap-1`}
                                >
                                  {groupReactions(msg.reactions).map(({ emoji, count }, index) => (
                                    <span
                                      key={index}
                                      className="
                                        w-auto px-2 h-8
                                        flex items-center justify-center
                                        text-lg font-medium
                                        rounded-full bg-gray-100 dark:bg-neutral-800 backdrop-blur shadow
                                      "
                                    >
                                      {emoji} {count > 1 && <span className="text-gray-900 text-xs ml-2">{formatCount(count)}</span>}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </SwipeMessage>
                      </div>
                    </div>
                  </motion.div>
                  
                  {(() => {
                    const seenBy = chatInfo?.members
                      ?.filter(m => m.user_id !== userId)
                      .filter(member => {
                        const lastSeenMessage = [...messages]
                          .reverse()
                          .find(m =>
                            (m.statuses || []).some(
                              s => s.user_id === member.user_id && s.status === 'seen'
                            )
                          );
                        return lastSeenMessage?.message_id === msg.message_id;
                      });

                    if (seenBy?.length > 0) {
                      return (
                        <div className="absolute right-0 -bottom-7 flex items-center space-x-1 text-xs text-gray-400 dark:text-gray-500">
                          {seenBy.slice(0, 3).map(member => (
                            <img
                              key={member.user_id}
                              src={member.profile_url || "/assets/images/default_profile.png"}
                              alt={member.username}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ))}
                          {seenBy.length > 3 && <span>+{seenBy.length - 3}</span>}
                        </div>
                      );
                    }

                    const isLastMessage = msg.message_id === messages[messages.length - 1]?.message_id;
                    if (isLastMessage && msg.is_sent_by_you) {
                      if (msg.pending) {
                        return (
                          <div className="absolute -bottom-5 right-0 text-xs text-gray-400 dark:text-gray-300">
                            Sending…
                          </div>
                        );
                      }
                      if (msg.failed) {
                        return (
                          <div className="absolute -bottom-5 right-0 text-xs text-red-500">
                            Failed to send
                          </div>
                        );
                      }
                      const delivered = (msg.statuses || []).some(
                        s => s.status === 'delivered' && s.user_id !== userId
                      );
                      return (
                        <div className="absolute -bottom-5 right-0 text-xs text-gray-400 dark:text-gray-300">
                          {delivered ? 'Delivered' : 'Sent'}
                        </div>
                      );
                    }

                    return null;
                  })()}
                </div>
              );
            })}
          </div>
        )}
        {isRecording && showTrash && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{ top: dragPos.y - 20, left: dragPos.x - 20 }}
          >
            <div className="p-2 rounded-full bg-black/60">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
          </div>
        )}
        {replyTo && (
          <div className="flex items-center justify-between bg-gray-100 dark:bg-neutral-800 p-2 rounded">
            <div className="text-sm flex-1 mr-2 overflow-hidden flex items-center">
              <span className="flex-shrink-0 mr-1 text-gray-700 dark:text-gray-300">Replying to:</span>
              <span className="font-bold truncate whitespace-nowrap text-black dark:text-white">
                {replyTo.type === 'audio'
                  ? 'Audio message'
                  : replyTo.type === 'photo'
                  ? 'Photo'
                  : replyTo.type === 'video'
                  ? 'Video'
                  : replyTo.type === 'file'
                  ? (replyTo.file_name || 'File')
                  : replyTo.type === 'audio call'
                  ? 'Audio call'
                  : replyTo.type === 'video call'
                  ? 'Video call'
                  : replyTo.content}
              </span>
            </div>
            <button
              onClick={() => setReplyTo(null)}
              className="text-red-500 flex-shrink-0 p-1"
            >
              <CircleX className="w-5 h-5" />
            </button>
          </div>
        )}

        {chatInfo?.is_group ? (
          chatInfo.members?.some(m => m.user_id === userId && !m.left_at) ? (
            <div className="sticky bottom-0 p-4 border-t border-gray-200 dark:border-neutral-800 bg-white dark:bg-darkCard flex items-center gap-2">
              <div className="flex items-center gap-2">
                <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={handlePickFile}>
                  <Paperclip className="w-5 h-5 text-white" />
                </button>
                
                <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={handlePickFromGallery}>
                  <PhotoIcon className="w-5 h-5 text-white" />
                </button>

                {Capacitor.isNativePlatform() && (
                  <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={openNativeCamera}>
                    <CameraIcon className="w-5 h-5 text-white" />
                  </button>
                )}
                
                <button
                  ref={micButtonRef}
                  className={`p-2 rounded-full flex items-center justify-center ${
                    isRecording ? 'bg-red-600' : 'bg-gray-600'
                  } select-none focus:outline-none focus:ring-0 active:outline-none`}
                  type="button"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onPointerDown={handleMicPointerDown}
                  onPointerUp={handleMicPointerUp}
                  style={{ WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
                >
                  <MicrophoneIcon className="w-5 h-5 text-white" />
                </button>

                {isRecording && (
                  <div className={`ml-2 ${recordingHint === "Release to discard" ? "text-gray-600" : "text-red-600"} animate-pulse`}>
                    {recordingHint}
                  </div>
                )}
              </div>

              {!isRecording && (
                <>
                  <textarea
                    ref={composerRef}
                    rows={1}
                    placeholder="Aa"
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      autoResizeComposer();
                    }}
                    onInput={autoResizeComposer}
                    className="flex-1 border border-gray-300 dark:border-neutral-700 rounded-3xl px-4 py-2 outline-none w-full bg-white dark:bg-neutral-800 placeholder-gray-400 dark:placeholder-neutral-500 text-black dark:text-white resize-none overflow-y-auto"
                    style={{ maxHeight: 60, WebkitOverflowScrolling: 'touch' }}
                  />

                  <button
                    onClick={sendMessage}
                    className="bg-chatBlueColor text-white p-2 rounded-full flex items-center justify-center"
                  >
                    <SendHorizonal className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="sticky bottom-0 p-4 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-darkCard text-center text-sm text-gray-500 dark:text-gray-300">
              You left the group. You can no longer send messages.
            </div>
          )
        ) : (
          <div className="sticky bottom-0 p-4 border-t border-gray-200 dark:border-neutral-800 bg-white dark:bg-darkCard flex items-center gap-2">
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={handlePickFile}>
                <Paperclip className="w-5 h-5 text-white" />
              </button>
              
              <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={handlePickFromGallery}>
                <PhotoIcon className="w-5 h-5 text-white" />
              </button>
 
              {Capacitor.isNativePlatform() && (
                <button className="p-2 rounded-full flex items-center justify-center bg-gray-600" onClick={openNativeCamera}>
                  <CameraIcon className="w-5 h-5 text-white" />
                </button>
              )}

              <button
                ref={micButtonRef}
                className={`p-2 rounded-full flex items-center justify-center ${
                  isRecording ? 'bg-red-600' : 'bg-gray-600'
                } select-none focus:outline-none focus:ring-0 active:outline-none`}
                type="button"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onPointerDown={handleMicPointerDown}
                onPointerUp={handleMicPointerUp}
                style={{ WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
              >
                <MicrophoneIcon className="w-5 h-5 text-white" />
              </button>

              {isRecording && (
                <div className={`ml-2 ${recordingHint === "Release to discard" ? "text-gray-600" : "text-red-600"} animate-pulse`}>
                  {recordingHint}
                </div>
              )}
            </div>

            {!isRecording && (
              <>
                <textarea
                  ref={composerRef}
                  rows={1}
                  placeholder="Aa"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    autoResizeComposer();
                  }}
                  onInput={autoResizeComposer}
                  className="flex-1 border border-gray-300 dark:border-neutral-700 rounded-3xl px-4 py-2 outline-none w-full bg-white dark:bg-neutral-800 placeholder-gray-400 dark:placeholder-neutral-500 text-black dark:text-white resize-none overflow-y-auto"
                  style={{ maxHeight: 60, WebkitOverflowScrolling: 'touch' }}
                />
              
                <button
                  onClick={sendMessage}
                  className="bg-chatBlueColor text-white p-2 rounded-full flex items-center justify-center"
                >
                  <SendHorizonal className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        )}

        {actionFor !== null && (
          <div
            className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-xl z-40"
            onClick={closeOverlays}
          />
        )}
      </motion.div>

      {openMedia && (
        <OpenMediaViewer openMedia={openMedia} onClose={() => setOpenMedia(null)} />
      )}
    </div>
  );
}